"use client";

import { useState } from "react";
import { Session } from "next-auth";
import { signOut } from "next-auth/react";
import { LogOut, ChevronDown, Users } from "lucide-react";
import { Button } from "../shadcn/button";
import { useTeamContext } from "@/contexts/TeamContext";
import { Avatar } from "@/components/ui/avatars/Avatar";
import { api } from "@/trpc/react";
import { getSafeProfileName } from "@/lib/userNames";

interface UserProfileDropdownProps {
  session: Session | null;
  workspaceSessionId?: string | null;
}

export function UserProfileDropdown({ session, workspaceSessionId }: UserProfileDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { currentTeamId, teams } = useTeamContext();
  const { data: currentUser } = api.users.getCurrent.useQuery(undefined, {
    enabled: !!session?.user?.id,
  });
  const { data: workspaceSession, isLoading: workspaceSessionLoading } = api.sessions.get.useQuery(
    { sessionId: workspaceSessionId! },
    { enabled: !!workspaceSessionId }
  );

  if (!session) {
    return null;
  }

  // While the workspace session is loading, render a skeleton to avoid flashing "Personal"
  // for team workspaces before the query resolves.
  if (workspaceSessionId && workspaceSessionLoading) {
    return <div className="h-9 w-24 animate-pulse rounded-lg bg-accent" />;
  }

  const userName = getSafeProfileName(session.user?.profileName);
  const userImage = typeof session.user?.image === "string" ? session.user.image : null;
  const userAvatarUrl = currentUser?.avatarUrl ?? null;
  const firstName = userName.split(" ")[0] || userName;
  const subscriptionTier = session.user?.subscriptionTier || "starter";

  // On workspace pages, use the session's teamId from the DB instead of the global TeamContext
  const effectiveTeamId = workspaceSessionId ? (workspaceSession?.teamId ?? null) : currentTeamId;
  const currentTeam = effectiveTeamId ? teams.find((t) => t.id === effectiveTeamId) : null;
  const teamAvatarUrl = currentTeam?.avatarUrl ?? null;

  return (
    <div className="relative">
      {/* Closed State: Avatar/Users Icon + Name */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-accent"
        aria-expanded={isOpen}
        aria-label="User menu"
      >
        {currentTeam ? (
          <>
            {teamAvatarUrl ? (
              <Avatar primaryUrl={teamAvatarUrl} name={currentTeam.name} size={32} />
            ) : (
              <span className="bg-muted h-8 w-8 rounded-full items-center justify-center flex">
                <Users className="h-5 w-5" />
              </span>
            )}
            <span className="hidden md:inline">{currentTeam.name}</span>
          </>
        ) : (
          <>
            <Avatar primaryUrl={userAvatarUrl} fallbackUrl={userImage} name={userName} size={32} />
            <span className="hidden md:inline">{firstName}</span>
          </>
        )}
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop to close on click outside */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* Dropdown Panel */}
          <div className="absolute right-0 top-full mt-2 w-64 z-50 rounded-xl border border-border bg-popover shadow-2xl">
            {/* User Profile Card */}
            <div className="border-b border-border p-4">
              <div className="flex items-center gap-3">
                <Avatar
                  primaryUrl={userAvatarUrl}
                  fallbackUrl={userImage}
                  name={userName}
                  size={32}
                />
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                    Welcome back
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-foreground">{userName}</p>
                    {currentTeam ? (
                      <p className=" tracking-wide text-muted-foreground">/ {currentTeam.name}</p>
                    ) : (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${
                          subscriptionTier === "max"
                            ? "bg-violet-500/20 text-violet-300 border border-violet-500/30"
                            : subscriptionTier === "plus"
                              ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                              : "bg-muted/50 text-muted-foreground border border-border"
                        }`}
                      >
                        {subscriptionTier}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Logout Section */}
            <div className="p-2 space-y-1">
              <Button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  void signOut({ callbackUrl: "/auth/signin" });
                }}
                variant="ghost"
                size="sm"
                className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10"
                startIcon={<LogOut className="h-4 w-4" />}
              >
                Log out
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
